import type { LabAcquisitionOffer } from '@/lib/api/labAcquisitionHooks';
import { useAuth } from '@/lib/auth/AuthProvider';
import { readStackPurchase } from './stackPurchase';
import { readPlanProgress } from './acceptQuarterlyPlans';
/**
 * Decides whether the post-purchase upsell overlay should open right now.
 *
 * Open conditions (ALL must hold):
 *   1. Patient has a purchase within the 24h eligibility window
 *      (`/me/recent-purchase` returns a row).
 *   2. Welcome has a treatment/plan/consult offer. Home can recover the same
 *      undismissed product list with its server-owned A lab offer appended.
 *   3. Patient hasn't already dismissed/converted this source product
 *      (localStorage `ppu_dismissed:<source_product_id>` for treatments,
 *      `ppu_lab_dismissed:<acquisition_id>` for the individual lab offer).
 *
 * The `post-purchase-upsell` experiment completed 2026-07-13 with variant-a
 * winning — the overlay is now default-on for every eligible purchase. The
 * experimentKey/variantKey stamps on the returned state stay for analytics
 * continuity.
 *
 * Dismissal is stored in localStorage (not sessionStorage) so it
 * persists across tab close, refresh, and re-opens within the same
 * browser. The /me/recent-purchase 24h window already gates against
 * showing the upsell to patients beyond their post-purchase moment, so
 * a stale localStorage flag from months ago is harmless — they
 * wouldn't be eligible anyway. (Cross-device persistence would need
 * backend; not worth the schema change for v1.)
 *
 * Returns null when any condition fails — caller renders nothing.
 */

import { useEffect, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

import { track } from '@/lib/analytics';
import {
  useBootstrap,
  useMarketplace,
  useMyExperimentAssignments,
} from '@/lib/api/hooks';
import {
  usePostPurchaseOffer,
  useRecentPurchase,
  useUpsellQuote,
  type ConsultUpsell,
  type PlanUpsell,
  type RecentPurchase,
  type UpsellQuoteItem,
} from '@/lib/api/upsellHooks';
import { getExperimentVariant } from '@/lib/experiments';

const EXPERIMENT_KEY = 'post-purchase-upsell';
const VARIANT_KEY = 'variant-a';
const DISMISS_KEY_PREFIX = 'ppu_dismissed:';
const LAB_DISMISS_KEY_PREFIX = 'ppu_lab_dismissed:';
// Quarterly plan-upsell step (shown BEFORE the treatment cards). Cohorted
// by the backend-assigned `quarterly-renewal-upsell-v1` experiment —
// control sees the drawer exactly as before.
export const PLAN_EXPERIMENT_KEY = 'quarterly-renewal-upsell-v1';
const PLAN_DISMISS_KEY_PREFIX = 'ppu_plan_dismissed:';
// Provider-consult membership step. Cohorted by the backend-assigned
// `consult-upsell-v1` experiment. Dismissal is per-browser (not per-sub —
// the membership is per-patient); the 24h recent-purchase window already
// bounds how long a stale flag matters.
export const CONSULT_EXPERIMENT_KEY = 'consult-upsell-v1';
const CONSULT_DISMISS_KEY = 'ppu_consult_dismissed';

function dismissKey(sourceProductId: string) {
  return `${DISMISS_KEY_PREFIX}${sourceProductId}`;
}

function planDismissKey(subscriptionId: string) {
  return `${PLAN_DISMISS_KEY_PREFIX}${subscriptionId}`;
}

/** Persist a plan-step resolution WITHOUT flipping this session's React
 *  state — used on acceptance so the confirmation screen stays mounted
 *  while future drawer opens skip the (now moot) pitch. */
export function persistPlanStepDismissal(subscriptionId: string) {
  try {
    window.localStorage.setItem(planDismissKey(subscriptionId), '1');
  } catch {
    /* private-mode storage failure — server-side ineligibility covers us */
  }
}

/** Same latch pattern for the consult step — acceptance persists directly
 *  so the confirmation screen survives the reactive flag flip; future
 *  opens skip the pitch (the server's already-member gate covers other
 *  browsers). */
export function persistConsultStepDismissal() {
  try {
    window.localStorage.setItem(CONSULT_DISMISS_KEY, '1');
  } catch {
    /* private-mode storage failure — server-side already-member covers us */
  }
}

function readStorage(key: string | null): string | null {
  if (typeof window === 'undefined' || !key) return null;
  try {
    return window.localStorage.getItem(key);
  } catch {
    // Safari private mode / SSR / disabled storage — treat as not dismissed.
    return null;
  }
}

function readDismissed(sourceCanonical: string | null): string | null {
  return readStorage(sourceCanonical ? dismissKey(sourceCanonical) : null);
}

export interface PostPurchaseUpsellState {
  labUpsell: LabAcquisitionOffer | null;
  open: boolean;
  experimentKey: string;
  variantKey: string;
  recentPurchase: ReturnType<typeof useRecentPurchase>['data'];
  items: ReturnType<typeof useUpsellQuote>['data'] extends infer T
    ? T extends { items: infer I }
      ? I
      : never
    : never;
  // Closes the drawer for this session. `dismissDrawer: false` skips the
  // persistent localStorage flag — used when an un-surfaced step (e.g.
  // the consult pitch behind the plan step) still deserves a turn on the
  // next open within the 24h window.
  close: (opts?: { dismissDrawer?: boolean }) => void;
  // Quarterly plan-upsell step — present (and showPlanStep=true) only for
  // variant-a of `quarterly-renewal-upsell-v1` on an eligible monthly
  // purchase. dismissPlanStep persists a decline so re-mounts within the
  // 24h window skip straight to the treatment cards.
  planUpsell: PlanUpsell | null;
  showPlanStep: boolean;
  planExperimentKey: string;
  planVariantKey: string | null;
  dismissPlanStep: () => void;
  // Provider-consult membership step — cohort+dismissal verdict only;
  // ORDERING (plan step first, consult after) is the overlay's job, so
  // showConsultStep deliberately ignores showPlanStep.
  consultUpsell: ConsultUpsell | null;
  showConsultStep: boolean;
  consultExperimentKey: string;
  consultVariantKey: string | null;
  dismissConsultStep: () => void;
}

export function useShouldShowPostPurchaseUpsell(): PostPurchaseUpsellState | null {
  const { session } = useAuth();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // Two overrides that force the drawer open with synthesized data
  // from `/me/marketplace` so we can iterate / QA without a real
  // Stripe purchase + experiment cookie:
  //
  //   - `?devUpsell=1` — DEV-build only (Vite tree-shakes in prod).
  //     For designers iterating locally; no auth gate.
  //   - `?qaUpsell=1`  — admin-only. Works in production too. Gated
  //     on `bootstrap.is_admin` so only signed-in admins can trigger
  //     it. A logged-out / non-admin visitor with the param does
  //     nothing (the override silently no-ops).
  //
  // Both share the same mock-data branch.
  const { data: bootstrap } = useBootstrap();
  const devOverride =
    import.meta.env.DEV && searchParams.get('devUpsell') === '1';
  const qaOverride =
    Boolean(bootstrap?.is_admin) && searchParams.get('qaUpsell') === '1';
  const override = devOverride || qaOverride;
  const [devDismissed, setDevDismissed] = useState(false);
  const [devPlanDismissed, setDevPlanDismissed] = useState(false);
  const [devConsultDismissed, setDevConsultDismissed] = useState(false);
  const marketplace = useMarketplace();

  // /onboarding/welcome is the first screen a new patient sees after
  // their checkout-claim completes — RequireOnboarded redirects every
  // not-yet-onboarded patient through /onboarding/welcome →
  // /onboarding/intake → /onboarding/expect → /onboarding/complete →
  // /app. The welcome screen is the closest analog to the CarePortals
  // "upsell fires after account created" moment. A completed marketing intake
  // can go straight to Home instead; recover the SAME undismissed product list
  // there, including its plan/consult steps, when the A lab offer arrives.
  const isWelcomeRoute = location.pathname.startsWith('/onboarding/welcome');
  const isHomeRoute = location.pathname === '/app' || location.pathname === '/app/';
  const isUpsellRoute = isWelcomeRoute || isHomeRoute;


  // Deliberately NOT gated on cohort: the backend cohort answer can land a
  // round-trip after mount, and gating would serialize the requests.
  // /me/post-purchase-offer returns the recent purchase AND its quote in
  // one call (they were inherently serial as two endpoints), and keeps a
  // brief while-null poll for the webhook-provisioning race.
  const offer = usePostPurchaseOffer({
    enabled: isUpsellRoute,
    // A fast onboarding completion can precede paid-offer binding. Refresh the
    // cached welcome response when Home mounts after its video/tour finishes.
    refetchOnMount: isHomeRoute ? 'always' : true,
    waitForLabOffer: isUpsellRoute,
  });
  const purchase = offer.data?.purchase ?? null;
  const ownedLabOffer = offer.data?.lab_upsell?.variant === 'variant-a' ? offer.data.lab_upsell : null;
  const labDismissKey = ownedLabOffer ? `${LAB_DISMISS_KEY_PREFIX}${ownedLabOffer.acquisition_id}` : null;
  const [labDismissed, setLabDismissed] = useState<string | null>(() => readStorage(labDismissKey));
  useEffect(() => setLabDismissed(readStorage(labDismissKey)), [labDismissKey]);
  const labUpsell = labDismissed === '1' || readStorage(labDismissKey) === '1' ? null : ownedLabOffer;
  const quoteItems = offer.data?.quote?.items ?? [];
  const sourceCanonical = purchase?.source_product_id ?? null;
  const pendingPurchase = purchase
    ? readStackPurchase(session?.user.id, purchase.source_product_id, purchase.purchased_at)
    : null;

  // Quarterly plan-upsell step. Server decides ELIGIBILITY (plan_upsell
  // non-null); the backend-assigned cohort decides VISIBILITY. A null
  // variant (assignments not resolved yet) shows nothing — same as
  // control — so a slow assignments fetch can't flash the step.
  //
  // Cohort read comes from the backend assignments QUERY (shared TanStack
  // key with AppLayout + ExperimentCohortSync — no extra request), NOT the
  // exp_assignments cookie. This hook's primary home is /onboarding/welcome
  // under OnboardingShell, which has no ExperimentsContext provider and
  // never writes the cookie — the cookie there is a stale marketing-site
  // snapshot that will never contain portal-audience keys. The cookie is
  // kept only as a paint-time fallback before the query resolves.
  // Keep the combined drawer alive if a lab purchase removes its offer from
  // a later server response. The remaining treatment choices still deserve a
  // turn. A persisted unfinished stack restores this latch after reload for
  // the same owner + exact purchase; a dismissed list stays closed.
  const recoveredHomePurchase = useRef<string | null>(null);
  const purchaseKey = purchase ? JSON.stringify([
    bootstrap?.patient?.id, sourceCanonical, purchase.purchased_at,
  ]) : null;
  const hasHomeProducts = Boolean(
    isHomeRoute && purchase?.has_upsells && quoteItems.length > 0 &&
    readDismissed(sourceCanonical) !== '1',
  );
  if (hasHomeProducts && (labUpsell || pendingPurchase)) recoveredHomePurchase.current = purchaseKey;
  const hasNormalOfferRoute = isWelcomeRoute || Boolean(
    hasHomeProducts && purchaseKey && recoveredHomePurchase.current === purchaseKey,
  );
  // A bundle purchase gets one offer for every member; the solo field stays
  // null for bundles so older clients never switch just one of them. Once
  // part of a stack has switched the server stops quoting it, so an
  // unfinished switch resumes from the quote the patient accepted.
  const incomingPlan = offer.data?.bundle_plan_upsell ?? offer.data?.plan_upsell ??
    (purchase ? readPlanProgress(session?.user.id, purchase.source_product_id, purchase.purchased_at)?.plan ?? null : null);
  const planSnapshot = useRef<{purchase: string; plan: PlanUpsell | null}>({purchase: '', plan: null});
  if (planSnapshot.current.purchase !== (purchaseKey ?? '')) {
    planSnapshot.current = {purchase: purchaseKey ?? '', plan: incomingPlan};
  } else if (!planSnapshot.current.plan && incomingPlan) {
    planSnapshot.current.plan = incomingPlan;
  }
  // Keep the displayed authorization terms and retry UI even if a background
  // refresh now sees one member already switched. Acceptance revalidates them.
  const planUpsell = hasNormalOfferRoute && !pendingPurchase ? planSnapshot.current.plan : null;
  const assignmentsQuery = useMyExperimentAssignments();
  const planVariant =
    assignmentsQuery.data?.assignments?.[PLAN_EXPERIMENT_KEY] ??
    getExperimentVariant(PLAN_EXPERIMENT_KEY);
  const planSubId = planUpsell?.subscription_id ?? null;
  const [planDismissed, setPlanDismissed] = useState<string | null>(() =>
    readStorage(planSubId ? planDismissKey(planSubId) : null),
  );
  useEffect(() => {
    setPlanDismissed(readStorage(planSubId ? planDismissKey(planSubId) : null));
  }, [planSubId]);
  const dismissPlanStep = () => {
    if (planSubId) {
      try {
        window.localStorage.setItem(planDismissKey(planSubId), '1');
      } catch {
        /* private-mode storage failure — worst case the step re-shows */
      }
    }
    setPlanDismissed('1');
  };

  // Exposure for BOTH cohorts the moment an eligible offer exists — the
  // control arm's exposure is "saw the drawer without the plan step", and
  // without it the acceptance-rate comparison has no denominator. Fired
  // once per subscription, only after the cohort answer is known so the
  // event carries a real variant label.
  const planExposureRef = useRef<string | null>(null);
  useEffect(() => {
    if (!planUpsell || !planVariant || planExposureRef.current === planSubId)
      return;
    planExposureRef.current = planSubId;
    track('$experiment_viewed', {
      experiment_key: PLAN_EXPERIMENT_KEY,
      variant_key: planVariant,
      source: 'quarterly_plan_upsell',
      product_id: planUpsell.product_id,
      savings_per_month_cents: planUpsell.savings_per_month_cents,
    });
  }, [planUpsell, planVariant, planSubId]);

  const showPlanStep = Boolean(
    planUpsell && planVariant === 'variant-a' && planDismissed !== '1',
  );

  // Provider-consult membership step — same gating shape as the plan
  // step: server decides eligibility (consult_upsell non-null), the
  // backend-assigned cohort decides visibility, localStorage remembers a
  // decline. Variant read mirrors the plan step's (assignments query
  // first, cookie as paint-time fallback — see the comment above).
  const consultUpsell = hasNormalOfferRoute && !pendingPurchase ? offer.data?.consult_upsell ?? null : null;
  const consultVariant =
    assignmentsQuery.data?.assignments?.[CONSULT_EXPERIMENT_KEY] ??
    getExperimentVariant(CONSULT_EXPERIMENT_KEY);
  const [consultDismissed, setConsultDismissed] = useState<string | null>(() =>
    readStorage(CONSULT_DISMISS_KEY),
  );
  const dismissConsultStep = () => {
    persistConsultStepDismissal();
    setConsultDismissed('1');
  };

  // Exposure for BOTH cohorts once an eligible offer + cohort answer
  // exist — control's exposure is "saw the drawer without the consult
  // step". Keyed per source purchase so a re-mount doesn't refire.
  const consultExposureRef = useRef<string | null>(null);
  useEffect(() => {
    if (
      !consultUpsell ||
      !consultVariant ||
      !sourceCanonical ||
      consultExposureRef.current === sourceCanonical
    )
      return;
    consultExposureRef.current = sourceCanonical;
    track('$experiment_viewed', {
      experiment_key: CONSULT_EXPERIMENT_KEY,
      variant_key: consultVariant,
      source: 'consult_upsell',
      source_product_id: sourceCanonical,
      price_cents: consultUpsell.price_cents,
    });
  }, [consultUpsell, consultVariant, sourceCanonical]);

  const showConsultStep = Boolean(
    consultUpsell && consultVariant === 'variant-a' && consultDismissed !== '1',
  );

  // QA escape hatch: `?ppu=force` (or `?ppu=reset` / `?ppu=1`) on
  // /onboarding/welcome wipes the localStorage dismissal flag for
  // the current source product so a refresh re-triggers the overlay.
  // This is prod-safe (unlike `devUpsell` above which is DEV-only):
  // it only flips a flag we own, can't bypass the experiment cohort
  // or recent-purchase gates, and only affects the requester's own
  // browser. Useful for testing the live UI without clearing storage
  // manually or invalidating the experiment cookie.
  const forceParam = (searchParams.get('ppu') || '').toLowerCase();
  const forceReset =
    forceParam === 'force' ||
    forceParam === 'reset' ||
    forceParam === '1' ||
    forceParam === 'true';
  useEffect(() => {
    if (!forceReset || !sourceCanonical) return;
    try {
      window.localStorage.removeItem(dismissKey(sourceCanonical));
      if (planSubId) window.localStorage.removeItem(planDismissKey(planSubId));
      window.localStorage.removeItem(CONSULT_DISMISS_KEY);
      if (labDismissKey) window.localStorage.removeItem(labDismissKey);
    } catch {
      /* private-mode storage failure — fine, we read null next */
    }
    if (planSubId) setPlanDismissed(null);
    setConsultDismissed(null);
    setLabDismissed(null);
    // Also resurrect a drawer soft-closed earlier in this same session —
    // force means "show me everything again", full stop.
    setSessionClosed(false);
  }, [forceReset, sourceCanonical, planSubId, labDismissKey]);

  // Dismissal flag tracked as React state so close() triggers a
  // re-render. Re-reads on sourceCanonical change so we pick up the
  // right key once recent-purchase resolves.
  const [dismissed, setDismissed] = useState<string | null>(() =>
    forceReset ? null : readDismissed(sourceCanonical),
  );
  useEffect(() => {
    setDismissed(forceReset ? null : readDismissed(sourceCanonical));
  }, [sourceCanonical, forceReset]);
  // Session-only closed latch: a soft close (dismissDrawer: false) keeps
  // the drawer shut for the rest of this page session without writing
  // the persistent flag — a refresh re-opens it, leading with whatever
  // step never got its turn.
  const [sessionClosed, setSessionClosed] = useState(false);

  if (override) {
    if (devDismissed) return null;
    const products = marketplace.data?.products ?? [];
    const src = products[0];
    if (!src) return null;
    const mockRecent: RecentPurchase = {
      source_product_id: src.canonical_product_id ?? src.slug,
      source_sanity_id: src.id,
      source_product_title: src.title,
      purchased_at: new Date().toISOString(),
      has_upsells: true,
      hero_image_url: src.hero_image_url,
    };
    // Round synth prices to whole dollars (nearest $5) so the preview
    // doesn't read as fractional/fake. Real `/upsell/quote` returns
    // integer cents straight from Sanity — the marketplace endpoint
    // just doesn't project quarterly/compare/renewal yet. Once those
    // land in MarketplaceProduct (or this preview swaps to hit
    // /upsell/quote directly), this synth math goes away.
    const roundDollars = (cents: number, multiplier: number) =>
      Math.round((cents * multiplier) / 500) * 500;
    const mockItems: UpsellQuoteItem[] = products.slice(1, 4).map((p) => {
      // Marketplace now projects description / quantity / value_props /
      // treatment_support / method directly from Sanity (alongside the
      // legacy subtitle+summary fields). Map them through 1:1 — the
      // upsell card reads the same shape as real `/upsell/quote`.
      // Pricing matrix beyond `monthly_price_cents` is still synthesized
      // here because marketplace doesn't surface quarterly/compare/
      // renewal yet.
      return {
      sanity_product_id: p.id,
      canonical_product_id: p.canonical_product_id,
      slug: p.slug,
      title: p.title,
      description: p.description || null,
      treatment_support: p.treatment_support || null,
      method: p.method || null,
      // `quantity` from Sanity is typically already a short dose label
      // (e.g. "2000mg"). If an editor put it in subtitle instead,
      // surface a stripped version of subtitle so the dose pill still
      // renders. Otherwise null lets the pill stay hidden.
      quantity:
        p.quantity ||
        (p.subtitle && p.subtitle === p.summary
          ? /\(([^)]+)\)/.exec(p.subtitle)?.[1] ?? p.subtitle
          : null),
      pricing_mode: 'intro',
      monthly_price_cents: p.price_cents,
      monthly_compare_price_cents: p.price_cents
        ? roundDollars(p.price_cents, 1.25)
        : null,
      monthly_renewal_price_cents: p.price_cents
        ? roundDollars(p.price_cents, 1.5)
        : null,
      quarterly_price_cents: p.price_cents
        ? roundDollars(p.price_cents, 2.6)
        : null,
      quarterly_compare_price_cents: p.price_cents
        ? roundDollars(p.price_cents, 3.5)
        : null,
      quarterly_renewal_price_cents: p.price_cents
        ? roundDollars(p.price_cents, 4)
        : null,
      hero_image_url: p.hero_image_url,
      value_props: (p.value_props ?? p.tags ?? []).slice(0, 4),
      };
    });
    if (mockItems.length === 0) return null;
    // Mock quarterly plan offer so `?devUpsell=1` / `?qaUpsell=1` exercise
    // the plan step without a real monthly gluta/NAD/serm purchase. When
    // the signed-in account DOES have a real eligible purchase, prefer its
    // server-computed offer — real numbers, and accepting genuinely
    // switches the (test) subscription, so admins can QA end-to-end.
    // (The mock's product name is just marketplace products[0] — it won't
    // match what the account actually bought.)
    const mockPlan: PlanUpsell = planUpsell ?? {
      subscription_id: 'sub_mock',
      product_id: mockRecent.source_product_id,
      product_title: mockRecent.source_product_title,
      monthly_renewal_cents: 19900,
      quarterly_renewal_cents: 50700,
      quarterly_per_month_cents: 16900,
      savings_per_month_cents: 3000,
      savings_per_quarter_cents: 9000,
      first_shipment_containers: 2,
      container_noun: 'vials',
      next_renewal_date: new Date(Date.now() + 28 * 86400_000)
        .toISOString()
        .slice(0, 10),
    };
    // Evaluation mock — prefers the real server offer for the same reason
    // as mockPlan. NOTE the accept action always hits the REAL endpoint (a
    // real one-time charge on the admin's saved card) — that's the point
    // of the QA path on staging's test-mode Stripe; on prod, decline
    // instead.
    const mockConsult: ConsultUpsell = consultUpsell ?? {
      title: 'Peptide Evaluation',
      price_cents: 9900,
      one_time: true,
      fakedoor: true,
    };
    return {
      labUpsell,
      open: true,
      experimentKey: EXPERIMENT_KEY,
      variantKey: VARIANT_KEY,
      recentPurchase: mockRecent,
      items: mockItems,
      close: () => setDevDismissed(true),
      planUpsell: mockPlan,
      showPlanStep: !devPlanDismissed,
      planExperimentKey: PLAN_EXPERIMENT_KEY,
      planVariantKey: 'variant-a',
      dismissPlanStep: () => setDevPlanDismissed(true),
      consultUpsell: mockConsult,
      showConsultStep: !devConsultDismissed,
      consultExperimentKey: CONSULT_EXPERIMENT_KEY,
      consultVariantKey: 'variant-a',
      dismissConsultStep: () => setDevConsultDismissed(true),
    };
  }

  if (!isUpsellRoute) return null;
  if (!purchase) return null;
  // Do not open a one-product/treatment offer while bundle legs are landing.
  if (offer.data?.plan_upsell_pending && !planUpsell) return null;
  // The treatment cards need upsellProducts[] configured; the plan and
  // consult steps don't. Any alone is enough to open the drawer — a
  // step-only drawer closes itself after its steps resolve.
  // A adds a fourth card to the existing offer; it never creates a second,
  // lab-only popup after the original product list was dismissed. On Home we
  // recover the combined list only if welcome never resolved that purchase.
  const showOtherOffers = dismissed !== '1' && readDismissed(sourceCanonical) !== '1';
  const hasTreatments = showOtherOffers && purchase.has_upsells && quoteItems.length > 0;
  if (isHomeRoute && (!hasNormalOfferRoute || !hasTreatments)) return null;
  if (!hasTreatments && !(showOtherOffers && (showPlanStep || showConsultStep))) return null;
  // Do not let the product list jump ahead of a qualifying quarterly/consult
  // step while its independent cohort query is still in flight.
  if (assignmentsQuery.isPending && (planUpsell || consultUpsell)) return null;
  if (sessionClosed) return null;

  return {
    labUpsell: hasTreatments ? labUpsell : null,
    open: true,
    experimentKey: EXPERIMENT_KEY,
    variantKey: VARIANT_KEY,
    recentPurchase: purchase,
    items: hasTreatments ? quoteItems : [],
    close(opts?: { dismissDrawer?: boolean }) {
      setSessionClosed(true);
      if ((opts?.dismissDrawer ?? true) && labDismissKey && labUpsell) {
        try {
          window.localStorage.setItem(labDismissKey, '1');
        } catch {
          /* browser persistence is optional; server eligibility still applies */
        }
        setLabDismissed('1');
      }
      if ((opts?.dismissDrawer ?? true) && sourceCanonical) {
        try {
          window.localStorage.setItem(dismissKey(sourceCanonical), '1');
        } catch {
          /* private-mode storage failure — fine, we'll just re-show */
        }
        setDismissed('1');
      }
    },
    planUpsell: showOtherOffers ? planUpsell : null,
    showPlanStep: showOtherOffers && showPlanStep,
    planExperimentKey: PLAN_EXPERIMENT_KEY,
    planVariantKey: planVariant,
    dismissPlanStep,
    consultUpsell: showOtherOffers ? consultUpsell : null,
    showConsultStep: showOtherOffers && showConsultStep,
    consultExperimentKey: CONSULT_EXPERIMENT_KEY,
    consultVariantKey: consultVariant,
    dismissConsultStep,
  };
}
